/**
 * Comprehensive cleanup manager for complete state reset on sign out
 * Ensures no user data, listeners, or session state persists between users
 */

import { getAuth } from 'firebase/auth';
import { wipeAllClientData } from './siteDataWipe';
import { STORAGE_KEYS } from '../constants';

interface CleanupStats {
  intervalsCleared: number;
  timeoutsCleared: number;
  listenersRemoved: number;
  callbacksExecuted: number;
  storageKeysRemoved: number;
  errors: string[];
}

class CleanupManager {
  private static instance: CleanupManager;
  private intervals: Set<NodeJS.Timeout> = new Set();
  private timeouts: Set<NodeJS.Timeout> = new Set();
  private eventListeners: Map<EventTarget, Map<string, Set<EventListener>>> = new Map();
  private cleanupCallbacks: Set<() => void | Promise<void>> = new Set();
  private abortControllers: Set<AbortController> = new Set();
  private isCleaningUp: boolean = false;

  static getInstance(): CleanupManager {
    if (!CleanupManager.instance) {
      CleanupManager.instance = new CleanupManager();
    }
    return CleanupManager.instance;
  }

  /**
   * Register an interval for cleanup
   */
  registerInterval(id: NodeJS.Timeout): void {
    this.intervals.add(id);
  }

  /**
   * Unregister an interval (if cleared manually)
   */
  unregisterInterval(id: NodeJS.Timeout): void {
    this.intervals.delete(id);
  }

  /**
   * Register a timeout for cleanup
   */
  registerTimeout(id: NodeJS.Timeout): void {
    this.timeouts.add(id);
  }

  /**
   * Unregister a timeout (if cleared manually)
   */
  unregisterTimeout(id: NodeJS.Timeout): void {
    this.timeouts.delete(id);
  }

  /**
   * Register an event listener for cleanup
   */
  registerEventListener(target: EventTarget, event: string, listener: EventListener): void {
    if (!this.eventListeners.has(target)) {
      this.eventListeners.set(target, new Map());
    }
    
    const targetListeners = this.eventListeners.get(target)!;
    if (!targetListeners.has(event)) {
      targetListeners.set(event, new Set());
    }
    
    targetListeners.get(event)!.add(listener);
  }

  /**
   * Unregister an event listener
   */
  unregisterEventListener(target: EventTarget, event: string, listener: EventListener): void {
    const targetListeners = this.eventListeners.get(target);
    if (targetListeners) {
      const eventListeners = targetListeners.get(event);
      if (eventListeners) {
        eventListeners.delete(listener);
        if (eventListeners.size === 0) {
          targetListeners.delete(event);
        }
      }
      if (targetListeners.size === 0) {
        this.eventListeners.delete(target);
      }
    }
  }

  /**
   * Register an abort controller for cleanup
   */
  registerAbortController(controller: AbortController): void {
    this.abortControllers.add(controller);
  }

  /**
   * Register a custom cleanup callback
   */
  registerCleanupCallback(callback: () => void | Promise<void>): () => void {
    this.cleanupCallbacks.add(callback);
    return () => this.cleanupCallbacks.delete(callback);
  }

  /**
   * Clear all user-specific localStorage keys
   */
  private clearUserLocalStorage(): number {
    let cleared = 0;
    const keysToRemove: string[] = [];
    
    // Collect all keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Remove all user-specific keys
        if (Object.values(STORAGE_KEYS).includes(key as any)) {
          keysToRemove.push(key);
        }
        // Remove namespaced keys (e.g., appCache:uid:*)
        if (key.includes('appCache:') || key.includes('user_') || key.includes('profile')) {
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove collected keys
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        cleared++;
      } catch (error) {
        console.warn(`Failed to remove localStorage key ${key}:`, error);
      }
    });
    
    return cleared;
  }

  /**
   * Reset all field managers
   */
  private async resetFieldManagers(): Promise<void> {
    // Reset BehaviorFieldManager
    try {
      const { behaviorFieldManager } = await import('./behaviorFieldManager');
      if (behaviorFieldManager && 'reset' in behaviorFieldManager) {
        (behaviorFieldManager as any).reset();
      }
      // Clear pending updates by triggering immediate flush
      if (behaviorFieldManager && 'flushAll' in behaviorFieldManager) {
        (behaviorFieldManager as any).flushAll();
      }
    } catch (error) {
      console.warn('Failed to reset BehaviorFieldManager:', error);
    }

    // Reset ExamplesFieldManager
    try {
      const { examplesFieldManager } = await import('./behaviorFieldManager');
      if (examplesFieldManager && 'reset' in examplesFieldManager) {
        (examplesFieldManager as any).reset();
      }
    } catch (error) {
      console.warn('Failed to reset ExamplesFieldManager:', error);
    }

    // Reset ToolsFieldManager
    try {
      const { toolsFieldManager, toolElementsFieldManager } = await import('./behaviorFieldManager');
      if (toolsFieldManager && 'reset' in toolsFieldManager) {
        (toolsFieldManager as any).reset();
      }
      if (toolElementsFieldManager && 'reset' in toolElementsFieldManager) {
        (toolElementsFieldManager as any).reset();
      }
    } catch (error) {
      console.warn('Failed to reset ToolsFieldManager:', error);
    }

    // Note: ModelsFieldManager seems to use getInstance() pattern
    // We'll need to check if it exists and has a reset method
    try {
      const modelsModule = await import('./modelsFieldManager');
      // Check if there's a singleton instance or getInstance method
      if ('getInstance' in modelsModule) {
        const instance = (modelsModule as any).getInstance();
        if (instance && 'reset' in instance) {
          instance.reset();
        }
      }
    } catch (error) {
      console.warn('Failed to reset ModelsFieldManager:', error);
    }

    // Reset any other field managers via event
    try {
      window.dispatchEvent(new CustomEvent('app:reset_field_managers'));
    } catch {}
  }

  /**
   * Perform complete cleanup with detailed tracking
   */
  async performCompleteCleanup(): Promise<CleanupStats> {
    if (this.isCleaningUp) {
      console.warn('⚠️ Cleanup already in progress, skipping duplicate call');
      return {
        intervalsCleared: 0,
        timeoutsCleared: 0,
        listenersRemoved: 0,
        callbacksExecuted: 0,
        storageKeysRemoved: 0,
        errors: ['Cleanup already in progress']
      };
    }

    this.isCleaningUp = true;
    const stats: CleanupStats = {
      intervalsCleared: 0,
      timeoutsCleared: 0,
      listenersRemoved: 0,
      callbacksExecuted: 0,
      storageKeysRemoved: 0,
      errors: []
    };

    console.log('🧹 Starting comprehensive cleanup...');
    const startTime = performance.now();

    try {
      // Phase 1: Stop active operations
      console.log('Phase 1: Stopping active operations...');
      
      // Abort all fetch requests
      this.abortControllers.forEach(controller => {
        try {
          controller.abort();
        } catch {}
      });
      this.abortControllers.clear();

      // Clear all intervals
      this.intervals.forEach(id => {
        try {
          clearInterval(id);
          stats.intervalsCleared++;
        } catch (error) {
          stats.errors.push(`Failed to clear interval: ${error}`);
        }
      });
      this.intervals.clear();

      // Clear all timeouts
      this.timeouts.forEach(id => {
        try {
          clearTimeout(id);
          stats.timeoutsCleared++;
        } catch (error) {
          stats.errors.push(`Failed to clear timeout: ${error}`);
        }
      });
      this.timeouts.clear();

      // Phase 2: Remove event listeners
      console.log('Phase 2: Removing event listeners...');
      this.eventListeners.forEach((events, target) => {
        events.forEach((listeners, event) => {
          listeners.forEach(listener => {
            try {
              target.removeEventListener(event, listener);
              stats.listenersRemoved++;
            } catch (error) {
              stats.errors.push(`Failed to remove listener: ${error}`);
            }
          });
        });
      });
      this.eventListeners.clear();

      // Phase 3: Execute cleanup callbacks
      console.log('Phase 3: Executing cleanup callbacks...');
      const callbacks = Array.from(this.cleanupCallbacks);
      for (const callback of callbacks) {
        try {
          await callback();
          stats.callbacksExecuted++;
        } catch (error) {
          stats.errors.push(`Cleanup callback error: ${error}`);
        }
      }
      this.cleanupCallbacks.clear();

      // Phase 4: Reset field managers
      console.log('Phase 4: Resetting field managers...');
      await this.resetFieldManagers();

      // Phase 5: Clear storage
      console.log('Phase 5: Clearing storage...');
      stats.storageKeysRemoved = this.clearUserLocalStorage();

      // Phase 6: Dispatch reset events
      console.log('Phase 6: Dispatching reset events...');
      try {
        // Reset all React state
        window.dispatchEvent(new CustomEvent('app:reset_all_state'));
        
        // Notify components to clear their state
        window.dispatchEvent(new CustomEvent('app:clear_component_state'));
        
        // Clear any cached data
        window.dispatchEvent(new CustomEvent('app:clear_cache'));
      } catch (error) {
        stats.errors.push(`Failed to dispatch reset events: ${error}`);
      }

      const endTime = performance.now();
      console.log(`✅ Cleanup completed in ${(endTime - startTime).toFixed(2)}ms`);
      console.log('Cleanup stats:', {
        ...stats,
        errors: stats.errors.length
      });

      if (stats.errors.length > 0) {
        console.warn('⚠️ Cleanup completed with errors:', stats.errors);
      }

    } catch (error) {
      console.error('❌ Critical error during cleanup:', error);
      stats.errors.push(`Critical error: ${error}`);
    } finally {
      this.isCleaningUp = false;
    }

    return stats;
  }

  /**
   * Verify cleanup was successful
   */
  async verifyCleanup(): Promise<boolean> {
    console.log('🔍 Verifying cleanup...');
    
    const issues: string[] = [];

    // Check for remaining intervals
    if (this.intervals.size > 0) {
      issues.push(`${this.intervals.size} intervals still registered`);
    }

    // Check for remaining timeouts
    if (this.timeouts.size > 0) {
      issues.push(`${this.timeouts.size} timeouts still registered`);
    }

    // Check for remaining event listeners
    if (this.eventListeners.size > 0) {
      let totalListeners = 0;
      this.eventListeners.forEach(events => {
        events.forEach(listeners => {
          totalListeners += listeners.size;
        });
      });
      issues.push(`${totalListeners} event listeners still registered`);
    }

    // Check for user data in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('user') || key.includes('profile') || key.includes('auth'))) {
        issues.push(`localStorage key still present: ${key}`);
      }
    }

    // Check Firebase auth state
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        issues.push('Firebase user still authenticated');
      }
    } catch {}

    if (issues.length > 0) {
      console.warn('⚠️ Cleanup verification failed:', issues);
      return false;
    }

    console.log('✅ Cleanup verification passed');
    return true;
  }

  /**
   * Get current cleanup manager state (for debugging)
   */
  getState(): {
    intervals: number;
    timeouts: number;
    eventListeners: number;
    callbacks: number;
    abortControllers: number;
    isCleaningUp: boolean;
  } {
    let eventListenerCount = 0;
    this.eventListeners.forEach(events => {
      events.forEach(listeners => {
        eventListenerCount += listeners.size;
      });
    });

    return {
      intervals: this.intervals.size,
      timeouts: this.timeouts.size,
      eventListeners: eventListenerCount,
      callbacks: this.cleanupCallbacks.size,
      abortControllers: this.abortControllers.size,
      isCleaningUp: this.isCleaningUp
    };
  }
}

// Export singleton instance
export default CleanupManager;

// Export convenience functions
export const cleanupManager = CleanupManager.getInstance();

export function registerInterval(id: NodeJS.Timeout): void {
  cleanupManager.registerInterval(id);
}

export function registerTimeout(id: NodeJS.Timeout): void {
  cleanupManager.registerTimeout(id);
}

export function registerEventListener(
  target: EventTarget,
  event: string,
  listener: EventListener
): void {
  cleanupManager.registerEventListener(target, event, listener);
}

export function registerCleanupCallback(
  callback: () => void | Promise<void>
): () => void {
  return cleanupManager.registerCleanupCallback(callback);
}

export async function performCompleteCleanup(): Promise<CleanupStats> {
  return cleanupManager.performCompleteCleanup();
}

export async function verifyCleanup(): Promise<boolean> {
  return cleanupManager.verifyCleanup();
}

// Auto-register in development mode for debugging
if (process.env.NODE_ENV === 'development') {
  (window as any).__cleanupManager = cleanupManager;
  console.log('CleanupManager available at window.__cleanupManager');
}
